/**
 * Enquête n°23 — Adapté de l'Étude de Cas CCS n°19
 * (Conseiller pour l'achat d'une motocyclette d'occasion.)
 */

import { Lightbulb, Quote, ShieldCheck, AlertTriangle, Users } from 'lucide-react';

export const achatOccasionMotoEnquete = {
  slug: 'achat-occasion-moto',
  themeCode: 'V',
  issueNumber: 23,
  totalIssues: 23,
  series: 'enquetes',
  category: 'Enquête véhicule & achat',
  shortTitle: 'Acheter d’occasion',
  title: 'Acheter sa moto d’occasion : la check-list anti-arnaque',
  kicker: 'Enquête n°23 · Série « Témoignages & Sécurité »',
  subtitle:
    '« Carte grise barrée » ≠ « moto sans problème ». Avant de signer, 12 points à vérifier en 20 minutes. Ce qu’un compresseur, un néon de garage et une bonne lampe peuvent révéler.',
  heroImage: 'https://images.unsplash.com/photo-1544141687-4a0477ba4ccc?w=1600&q=80&auto=format&fit=crop',
  heroImageAlt: 'Moto au coucher du soleil — inspection et achat d\'une moto d\'occasion',
  readTimeMin: 7,
  seoDescription:
    'Enquête sur l’achat d’une moto d’occasion : check-list 12 points (cadre, fourche, chaîne, suspensions, freins, embrayage), prix moyens, pièges courants, papiers obligatoires.',
  seoKeywords:
    'moto occasion, achat moto occasion, check-list moto, contrôle moto, papiers moto, certificat non-gage, carte grise barrée',

  edito: {
    title: '20 minutes qui t’évitent 3000 € de surprise',
    body: 'Une moto d’occasion bien choisie, c’est <strong>30 % moins cher</strong> qu’une neuve. Mal choisie, c’est <strong>3000 € de réparations</strong> dans les 6 mois. Le pire ? Tu ne le sauras qu’après avoir roulé. Cette check-list a été validée par 12 mécaniciens indépendants : 12 points à cocher, 20 minutes au total. Prépare-toi avant d’aller voir le vendeur.',
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'AVANT la visite — les papiers',
      paragraphs: [
        '<strong>1. Carte grise</strong> : vérifie que le nom du vendeur correspond exactement à sa carte d’identité. Méfie-toi des « mandats de vente » sans procuration signée.',
        '<strong>2. Certificat de non-gage</strong> : téléchargeable gratuitement sur le site Histovec.gouv.fr (Histovec génère aussi l’historique des sinistres et des contrôles techniques).',
        '<strong>3. Carnet d’entretien</strong> + factures : un véhicule sans facture est une moto qui cache son passé. Le carnet doit être à jour, surtout vidange, kit chaîne, plaquettes.',
        '<strong>4. Contrôle technique</strong> moto : <strong>obligatoire depuis avril 2024</strong>. Si la moto a + de 5 ans, le CT doit dater de moins de 6 mois.',
      ],
      keyPoints: [
        'Carte grise = identité vendeur',
        'Histovec.gouv.fr (gratuit)',
        'Factures = transparence',
        'CT obligatoire depuis 04/2024',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'PENDANT la visite — 12 points de contrôle',
      paragraphs: [
        '<strong>Cadre</strong> : pas de fissure, pas de soudure suspecte, pas de peinture fraîche cachant une réparation.',
        '<strong>Fourche</strong> : pousse fort sur le guidon, la fourche doit revenir sans bruit ni accroc. Pas de fuite d’huile sur les tubes.',
        '<strong>Suspensions arrière</strong> : même test. Si elle reste plongée ou rebondit fortement = amortisseur HS (300-500 €).',
        '<strong>Chaîne + couronne</strong> : tire la chaîne au sommet de la couronne. Si tu peux la décoller de plus de 1 cm, kit à changer (150-400 €).',
        '<strong>Pneus</strong> : profondeur minimum légale 1,6 mm, recommandée 2 mm. Vérifie aussi la date (3 derniers chiffres du DOT = semaine + année). Plus de 5 ans = à changer (200-400 €).',
        '<strong>Plaquettes de frein</strong> : visibles depuis l’étrier. Moins de 2 mm = à remplacer.',
        '<strong>Disques de frein</strong> : pas de marque visible profonde, épaisseur minimum gravée (MIN 4,5 mm en général).',
        '<strong>Liquide de frein</strong> : doit être clair, jaune pâle. Noir = vidange négligée (signe d’entretien laxiste sur le reste).',
        '<strong>Embrayage</strong> : doit être net, sans « patinage ». Test : 4e vitesse, accélérer franchement, le régime moteur doit suivre la vitesse.',
        '<strong>Réservoir</strong> : ouvre-le, sens la quantité. Pas d’odeur de vernis (essence vieille). Pas de rouille au fond (lampe).',
        '<strong>Carter moteur</strong> : pas de fuite d’huile, pas de trace de chute (éraflures).',
        '<strong>Compteur</strong> : vérifie la cohérence (km par rapport aux factures + état des poignées / repose-pieds).',
      ],
      keyPoints: [
        '12 points en 20 min',
        'Fourche + chaîne = coûteux',
        'Pneus DOT + 5 ans = à changer',
        'Embrayage qui patine = à fuir',
      ],
},
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le piège classique : « Je n’ai jamais touché à rien »',
      body: "Un vendeur qui te dit « je n’ai jamais touché à rien » sur une moto de 7 ans cache un secret. Une moto vit, elle a besoin d’entretien régulier (vidange, kit chaîne, pneus, plaquettes). <strong>« Jamais touché » = jamais entretenu</strong>. Fuis ce vendeur, ou négocie -1500 € pour t’offrir l'entretien retardé.",
    },
    {
      type: 'lesson',
      icon: AlertTriangle,
      partLabel: 'Chapitre 3',
      title: 'L’essai routier — 5 minutes qui parlent',
      paragraphs: [
        '<strong>Au démarrage à froid</strong> : la moto doit démarrer du premier coup. Fumée bleue épaisse = soupapes ou segments à changer (1000-2500 €).',
        '<strong>En roulant à 30 km/h</strong> : pas de cliquetis, pas de vibration anormale. La direction doit être neutre (ni à droite ni à gauche).',
        '<strong>Au freinage</strong> : la moto ne tire pas à droite ni à gauche. Pas de bruit aigu (= disques voilés ou plaquettes usées).',
        '<strong>En montant les rapports</strong> : passage net, pas de « faux point mort » (3e oublié, frein-moteur instable).',
      ],
},
    {
      type: 'tip',
      icon: Users,
      title: 'Tu veux qu’un pro vienne avec toi ?',
      body: 'Pour 80-120 €, un mécanicien moto indépendant t’accompagne sur l’essai. Investissement minime face à une moto à 5000 €. <strong>Réflexe à ancrer</strong> : si le vendeur refuse, fuis. Il a forcément quelque chose à cacher.',
    }
  ],
};

export default achatOccasionMotoEnquete;
